import { useMemo, useState, useRef, useEffect, type FormEvent, type KeyboardEventHandler, type RefObject } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import { WebMcpForm } from '@/components/WebMcpForm';
import { useCartStore } from '@/stores/cartStore';
import { normalizeCartLines } from '@shopgnd/shared';
import { useAuthStore } from '@/stores/authStore';
import { useThemeStore } from '@/stores/themeStore';
import { FlagTricolorBar } from '@/components/flag-tricolor-bar';
import { ShopGndLogo } from '@/components/ShopGndLogo';
import { CurrencySelector } from '@/components/CurrencySelector';
import { AccountMenu } from '@/components/AccountMenu';
import { NotificationBell } from '@/components/NotificationBell';
import { useHubAccess } from '@/hooks/useHubAccess';
import { useFeatureFlag } from '@/hooks/usePlatformConfig';
import {
  buildScopedSearchUrl,
  LISTING_SEARCH_SCOPES,
  listingScopeFromPathname,
  listingSearchScopesForFlags,
  type ListingSearchScope,
} from '@/lib/listingSearchScope';
import { storefrontNavIsActive, storefrontNavStripLinks } from '@/lib/storefrontNavigation';

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg className={clsx('h-4 w-4', className)} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
      <circle cx="11" cy="11" r="7" />
      <path d="M20 20l-3-3" strokeLinecap="round" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
    </svg>
  );
}

function CartIcon() {
  return (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path d="M6 6h15l-1.5 9h-12z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <circle cx="9" cy="20" r="1.25" fill="currentColor" />
      <circle cx="17" cy="20" r="1.25" fill="currentColor" />
      <path d="M3 3h2l1 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function HeartIcon() {
  return (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden>
      <circle cx="12" cy="12" r="4" />
      <path strokeLinecap="round" d="M12 2v2m0 16v2M4.93 4.93l1.41 1.41m11.32 11.32 1.41 1.41M2 12h2m16 0h2M4.93 19.07l1.41-1.41m11.32-11.32 1.41-1.41" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
    </svg>
  );
}

function ChevronDownIcon({ className }: { className?: string }) {
  return (
    <svg
      className={clsx('h-3.5 w-3.5 shrink-0', className)}
      viewBox="0 0 20 20"
      fill="currentColor"
      aria-hidden
    >
      <path
        fillRule="evenodd"
        d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.08z"
        clipRule="evenodd"
      />
    </svg>
  );
}

const LISTING_HUB_PATHS = ['/search', '/grenada/shop', '/grenada/real-estate', '/grenada/classifieds', '/grenada/vendors'] as const;

function NavbarSearchForm({
  searchVal,
  searchScope,
  searchScopes,
  onSearchValChange,
  onScopeChange,
  onSubmit,
  compact = false,
  inputRef,
  onInputKeyDown,
}: {
  searchVal: string;
  searchScope: ListingSearchScope;
  searchScopes: typeof LISTING_SEARCH_SCOPES;
  onSearchValChange: (value: string) => void;
  onScopeChange: (scope: ListingSearchScope) => void;
  onSubmit: (e: FormEvent) => void;
  compact?: boolean;
  inputRef?: RefObject<HTMLInputElement>;
  onInputKeyDown?: KeyboardEventHandler<HTMLInputElement>;
}) {
  const barHeight = 'h-9';
  const textLeading = 'leading-9';
  const scopeDef = searchScopes.find((s) => s.id === searchScope) ?? searchScopes[0]!;

  return (
    <WebMcpForm
      toolName="search-marketplace"
      toolDescription="Search ShopGND products, classifieds, businesses, or properties in Grenada by keyword and listing type."
      toolAutoSubmit
      onSubmit={onSubmit}
      className="flex w-full min-w-0 items-stretch"
    >
      <div
        className={clsx(
          'relative shrink-0 overflow-hidden rounded-none border border-r-0 border-accent-600 bg-accent-500',
          barHeight,
          compact ? 'w-[5.5rem]' : 'w-[7rem]',
        )}
      >
        <select
          name="scope"
          value={searchScope}
          onChange={(e) => onScopeChange(e.target.value as ListingSearchScope)}
          className={clsx(
            'h-full w-full cursor-pointer appearance-none rounded-none bg-transparent',
            'truncate pl-2 pr-5 text-[11px] font-semibold text-brand-900',
            textLeading,
            'focus:outline-none',
          )}
          aria-label="Search scope"
        >
          {searchScopes.map((s) => (
            <option key={s.id} value={s.id}>
              {s.label}
            </option>
          ))}
        </select>
        <ChevronDownIcon className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-brand-900/70" />
      </div>
      <input
        ref={inputRef}
        name="q"
        value={searchVal}
        onChange={(e) => onSearchValChange(e.target.value)}
        onKeyDown={onInputKeyDown}
        placeholder={compact ? 'Search…' : scopeDef.placeholder}
        className={clsx(
          barHeight,
          textLeading,
          'min-w-0 flex-1 rounded-none border border-l-0 border-accent-600 bg-white/10 px-3 text-sm text-white',
          'placeholder:text-white/50 focus:bg-white/15 focus:outline-none',
        )}
        aria-label="Search"
      />
      <button
        type="submit"
        className={clsx(
          barHeight,
          'flex shrink-0 items-center justify-center rounded-none border border-l-0 border-accent-600 bg-accent-500 px-3.5 text-brand-900 transition hover:bg-accent-400',
        )}
        aria-label="Search"
      >
        <SearchIcon />
      </button>
    </WebMcpForm>
  );
}

const navStripLinkClass = ({ isActive }: { isActive: boolean }) =>
  clsx(
    'shrink-0 whitespace-nowrap px-3 py-1.5 text-sm font-semibold transition',
    isActive ? 'bg-brand-900 text-white shadow-sm' : 'text-brand-900 hover:bg-brand-900/10',
  );

export function Navbar() {
  const count = useCartStore((s) => normalizeCartLines(s.lines).reduce((n, l) => n + l.quantity, 0));
  const user = useAuthStore((s) => s.user);
  const hub = useHubAccess();
  const theme = useThemeStore((s) => s.theme);
  const toggleTheme = useThemeStore((s) => s.toggle);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchVal, setSearchVal] = useState('');
  const [searchScope, setSearchScope] = useState<ListingSearchScope>(() =>
    listingScopeFromPathname(location.pathname),
  );
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const mobileInputRef = useRef<HTMLInputElement>(null);
  const businessDirectoryEnabled = useFeatureFlag('businessDirectory');
  const propertyListingsEnabled = useFeatureFlag('propertyListings');
  const classifiedsEnabled = useFeatureFlag('classifieds');

  const navStripLinks = useMemo(
    () =>
      storefrontNavStripLinks({
        propertyListings: propertyListingsEnabled,
        classifieds: classifiedsEnabled,
        businessDirectory: businessDirectoryEnabled,
      }),
    [propertyListingsEnabled, classifiedsEnabled, businessDirectoryEnabled],
  );

  const searchScopes = useMemo(
    () =>
      listingSearchScopesForFlags({
        propertyListings: propertyListingsEnabled,
        classifieds: classifiedsEnabled,
        businessDirectory: businessDirectoryEnabled,
      }),
    [propertyListingsEnabled, classifiedsEnabled, businessDirectoryEnabled],
  );

  useEffect(() => {
    if (mobileSearchOpen) {
      mobileInputRef.current?.focus();
    }
  }, [mobileSearchOpen]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q');
    let scope = listingScopeFromPathname(location.pathname);
    if (!searchScopes.some((s) => s.id === scope)) {
      const fallback = searchScopes.find((s) => s.id === 'all') ?? searchScopes[0];
      if (fallback) scope = fallback.id;
    }
    setSearchScope(scope);
    if (q) setSearchVal(q);
    else if (LISTING_HUB_PATHS.includes(location.pathname as (typeof LISTING_HUB_PATHS)[number])) {
      setSearchVal('');
    }
  }, [location.pathname, location.search, searchScopes]);

  const navigateScopedSearch = (scope: ListingSearchScope, q: string) => {
    const sector = scope === 'vendors' ? new URLSearchParams(location.search).get('sector') ?? 'shop' : undefined;
    navigate(buildScopedSearchUrl(scope, q, { sector: sector ?? undefined }));
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchVal.trim();
    setMobileSearchOpen(false);
    navigateScopedSearch(searchScope, q);
  };

  const handleSearchScopeChange = (scope: ListingSearchScope) => {
    setSearchScope(scope);
    navigateScopedSearch(scope, searchVal.trim());
  };

  return (
    <header className="sticky top-0 z-50">
      <FlagTricolorBar />

      {/* ── Main bar ── */}
      <div className="bg-brand-800 text-white shadow-md">
        <div className="mx-auto grid max-w-listing grid-cols-[auto_auto] items-center justify-between gap-2 px-3 py-2 sm:px-4 sm:py-2.5 md:grid-cols-[auto_minmax(0,1fr)_auto] md:gap-4 lg:grid-cols-[auto_minmax(0,1fr)_auto]">

          <ShopGndLogo to="/" className="h-9 sm:h-10 md:h-11 lg:h-12" priority={location.pathname === '/'} />

          {/* Search — desktop, centered in header */}
          <div className="hidden min-w-0 justify-self-stretch px-2 md:block md:w-full md:max-w-none md:px-4">
            <NavbarSearchForm
              searchVal={searchVal}
              searchScope={searchScope}
              searchScopes={searchScopes}
              onSearchValChange={setSearchVal}
              onScopeChange={handleSearchScopeChange}
              onSubmit={handleSearch}
            />
          </div>

          {/* Actions */}
          <div className="flex shrink-0 items-center justify-self-end gap-0.5 sm:gap-1">
            {/* Mobile search toggle */}
            <button
              type="button"
              aria-label={mobileSearchOpen ? 'Close search' : 'Search'}
              onClick={() => setMobileSearchOpen((v) => !v)}
              className="rounded-lg p-2 text-white/80 transition hover:bg-white/10 hover:text-white md:hidden"
            >
              {mobileSearchOpen ? <XIcon /> : <SearchIcon />}
            </button>

            {/* Wishlist */}
            <Link
              to="/wishlist"
              aria-label="Wishlist"
              className="hidden rounded-lg p-2 text-white/80 transition hover:bg-white/10 hover:text-white sm:block"
            >
              <HeartIcon />
            </Link>

            {/* Cart */}
            <Link
              to="/cart"
              aria-label="Cart"
              className="relative rounded-lg p-2 text-white/80 transition hover:bg-white/10 hover:text-white"
            >
              <CartIcon />
              {count > 0 && (
                <span className="absolute right-0.5 top-0.5 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-accent-500 px-0.5 text-[10px] font-bold text-brand-900">
                  {count}
                </span>
              )}
            </Link>

            <NotificationBell />
            <AccountMenu />

            {/* Business Center quick link */}
            {user && hub.ready && hub.hasAnyHub ? (
              <Link
                to={hub.defaultPath}
                className="hidden rounded-lg bg-accent-500 px-2.5 py-1.5 text-xs font-bold text-brand-900 transition hover:bg-accent-600 lg:inline-flex"
              >
                Business Center
              </Link>
            ) : null}

            {/* Theme toggle */}
            <button
              type="button"
              onClick={toggleTheme}
              aria-label="Toggle theme"
              className="rounded-lg p-2 text-white/60 transition hover:bg-white/10 hover:text-white"
            >
              {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
            </button>

            <CurrencySelector className="hidden sm:flex" />
          </div>
        </div>

        {/* ── Mobile search bar — slides open below the main bar ── */}
        {mobileSearchOpen && (
          <div className="border-t border-white/10 px-3 py-2.5 md:hidden">
            <NavbarSearchForm
              compact
              searchVal={searchVal}
              searchScope={searchScope}
              searchScopes={searchScopes}
              onSearchValChange={setSearchVal}
              onScopeChange={handleSearchScopeChange}
              onSubmit={handleSearch}
              inputRef={mobileInputRef}
              onInputKeyDown={(e) => e.key === 'Escape' && setMobileSearchOpen(false)}
            />
          </div>
        )}
      </div>

      {/* ── Nav strip — Shop · Properties · Classifieds · Businesses · Find a pro · Deals ── */}
      <nav className="relative z-0 hidden bg-accent-500 md:block">
        <div className="mx-auto flex max-w-listing items-center gap-0.5 overflow-x-auto px-3">
          {navStripLinks.map((link) => (
            <Link
              key={link.id}
              to={link.to}
              className={navStripLinkClass({
                isActive: storefrontNavIsActive(location.pathname, link),
              })}
            >
              {link.label}
            </Link>
          ))}

          <Link
            to="/advertise"
            className={navStripLinkClass({ isActive: location.pathname === '/advertise' })}
          >
            Advertise
          </Link>

          {user && hub.ready && hub.hasAnyHub ? (
            <>
              <span className="mx-1 h-3.5 w-px shrink-0 bg-brand-900/20" />
              <Link
                to={hub.defaultPath}
                className="shrink-0 whitespace-nowrap px-3 py-1.5 text-sm font-bold text-brand-900 hover:bg-brand-900/10"
              >
                Business Center
              </Link>
            </>
          ) : null}
        </div>
      </nav>
    </header>
  );
}
